import { createFileRoute, Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, ArrowLeft, Ticket, Loader2, Wine, AlertTriangle, Minus, Plus } from "lucide-react";

import { EVENT_RULES_BY_LANG } from "@/lib/event-rules";
import { useI18n } from "@/lib/i18n";
import { useAutoTranslate } from "@/lib/auto-translate";

import { generateTicketCode } from "@/lib/ticket-utils";
import { toast } from "sonner";

export const Route = createFileRoute("/events/$eventId")({
  head: () => ({ meta: [{ title: "Etkinlik — JUËN" }] }),
  component: EventDetailPage,
});

interface EventRow {
  id: string;
  name: string;
  description: string | null;
  starts_at: string;
  ends_at: string | null;
  venue: string | null;
  cover_image_url: string | null;
  is_invite_only: boolean;
  door_min_topup_kurus: number;
  min_age: number;
}

interface TicketTypeRow {
  id: string;
  name: string;
  description: string | null;
  price_kurus: number;
  quantity: number;
  coming_soon?: boolean;
}


interface DrinkRow {
  id: string;
  name: string;
  description: string | null;
  price_kurus: number;
  volume_ml: number;
  abv_percent: number;
}

function EventDetailPage() {
  const { eventId } = Route.useParams();
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const { t, lang, locale } = useI18n();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [event, setEvent] = useState<EventRow | null>(null);
  const [tickets, setTickets] = useState<TicketTypeRow[]>([]);
  const [drinks, setDrinks] = useState<DrinkRow[]>([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const tc = useAutoTranslate([event?.name, event?.description, event?.venue, ...tickets.flatMap((tk) => [tk.name, tk.description])]);

  const getQty = (t: TicketTypeRow) => Math.max(1, Math.min(quantities[t.id] ?? 1, t.quantity));
  const setQty = (t: TicketTypeRow, q: number) => {
    setQuantities((prev) => ({ ...prev, [t.id]: Math.max(1, Math.min(t.quantity, q)) }));
  };

  const userAge = (() => {
    const bd = profile?.birth_date;
    if (!bd) return null;
    const d = new Date(bd);
    if (isNaN(d.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
    return age;
  })();
  const minAge = event?.min_age ?? 0;
  const isUnderAge = minAge > 0 && userAge !== null && userAge < minAge;
  const guardAge = () => {
    if (isUnderAge) {
      toast.error(t("events.ageLimitToast", { minAge }));
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (!loading && !user) void navigate({ to: "/login" });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    void (async () => {
      const [{ data: ev }, { data: tk }, { data: dr }] = await Promise.all([
        supabase.from("events").select("*").eq("id", eventId).maybeSingle(),
        supabase.from("ticket_types").select("*").eq("event_id", eventId).order("sort_order"),
        supabase.from("drinks").select("*").or(`event_id.eq.${eventId},event_id.is.null`).eq("is_active", true).order("sort_order"),
      ]);
      setEvent(ev as EventRow | null);
      const allTickets = (tk as (TicketTypeRow & { is_active?: boolean })[]) ?? [];
      setTickets(allTickets.filter((t) => t.is_active !== false));
      setDrinks((dr as DrinkRow[]) ?? []);
      setPageLoading(false);
    })();
  }, [eventId, user]);

  const handleClaimInvite = async () => {
    if (!user || !event) return;
    if (!guardAge()) return;
    setClaimingId("__invite__");
    try {
      const { data, error } = await supabase.rpc("claim_invite_ticket", { _event_id: eventId });
      if (error) throw error;
      const result = data as { success: boolean; ticket_id: string; already: boolean };
      if (!result?.ticket_id) throw new Error(t("events.inviteCreateFailed"));
      toast.success(result.already ? t("events.inviteAlreadyReady") : t("events.inviteReady"));
      void navigate({ to: "/tickets/$ticketId", params: { ticketId: result.ticket_id } });
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("common.error");
      toast.error(msg);
    } finally {
      setClaimingId(null);
    }
  };

  const handleGetTicket = async (t: TicketTypeRow) => {
    if (!user) return;
    if (!guardAge()) return;
    const qty = getQty(t);
    // Paid tickets still go through checkout
    if (t.price_kurus > 0) {
      try {
        sessionStorage.setItem(`ticket-qty:${t.id}`, String(qty));
      } catch {}
      void navigate({
        to: "/events/$eventId/checkout/$ticketTypeId",
        params: { eventId, ticketTypeId: t.id },
      });
      return;
    }

    // Free tickets: create order + ticket directly
    setClaimingId(t.id);
    try {
      // RLS requires INSERT with status='pending'
      const { data: order, error: orderErr } = await supabase
        .from("orders")
        .insert({
          user_id: user.id,
          event_id: eventId,
          ticket_type_id: t.id,
          quantity: 1,
          total_kurus: 0,
          status: "pending",
        })
        .select()
        .single();

      if (orderErr || !order) throw orderErr ?? new Error(translateOrderFail());

      // Mark free order as paid (allowed by policy when total_kurus = 0)
      const { error: updateErr } = await supabase
        .from("orders")
        .update({ status: "paid", paid_at: new Date().toISOString() })
        .eq("id", order.id);
      if (updateErr) throw updateErr;

      const { data: ticket, error: ticketErr } = await supabase
        .from("tickets")
        .insert({
          order_id: order.id,
          event_id: eventId,
          ticket_type_id: t.id,
          user_id: user.id,
          code: generateTicketCode(),
        })
        .select()
        .single();

      if (ticketErr || !ticket) throw ticketErr ?? new Error(t2("events.ticketCreateFailed"));

      toast.success(t2("events.ticketReady"));
      void navigate({ to: "/tickets/$ticketId", params: { ticketId: ticket.id } });
    } catch (err) {
      const msg = err instanceof Error ? err.message : t2("common.error");
      toast.error(msg);
    } finally {
      setClaimingId(null);
    }
  };

  function translateOrderFail() {
    return t("events.orderCreateFailed");
  }
  function t2(key: string) {
    return t(key);
  }

  if (
    pathname.includes(`/events/${eventId}/checkout/`) ||
    pathname.endsWith(`/events/${eventId}/drinks`)
  ) {
    return <Outlet />;
  }

  if (loading || !user || pageLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted-foreground">{t("common.loading")}</div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-2xl px-4 py-16 text-center">
          <h1 className="text-2xl font-bold">{t("events.notFoundTitle")}</h1>
          <Button asChild className="mt-4">
            <Link to="/events">{t("events.backToEventsBtn")}</Link>
          </Button>
        </div>
      </div>
    );
  }

  const isApproved = profile?.membership_status === "approved";
  const activeRules = EVENT_RULES_BY_LANG[lang] ?? EVENT_RULES_BY_LANG.tr;

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-4xl px-4 py-10">
        <Link to="/events" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> {t("events.backToEvents")}
        </Link>

        {event.cover_image_url && (
          <div className="mb-6 aspect-[16/9] w-full overflow-hidden rounded-2xl bg-muted">
            <img src={event.cover_image_url} alt={event.name} className="h-full w-full object-cover" />
          </div>
        )}

        <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{tc(event.name)}</h1>
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="h-4 w-4" />
            {new Date(event.starts_at).toLocaleString(locale, { dateStyle: "full", timeStyle: "short" })}
          </span>
          {event.venue && (
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-4 w-4" />
              {tc(event.venue)}
            </span>
          )}
        </div>

        {event.description && (
          <p className="mt-6 whitespace-pre-line text-base leading-relaxed text-muted-foreground">{tc(event.description)}</p>
        )}

        <div className="mt-10">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-xl font-semibold">{event.is_invite_only ? t("events.participation") : t("events.tickets")}</h2>
            <div className="flex flex-wrap gap-2">
              {isApproved && drinks.length > 0 && (
                <Button asChild variant="outline" size="sm">
                  <Link to="/events/$eventId/drinks" params={{ eventId }}>
                    <Wine className="mr-1.5 h-4 w-4" /> {t("events.drinks")}
                  </Link>
                </Button>
              )}
            </div>
          </div>

          <div className="mt-4">
            {!isApproved ? (
              <div className="rounded-2xl border border-warning/30 bg-warning/10 p-6 text-sm">
                {t("events.approvalRequired")}
              </div>
            ) : isUnderAge ? (
              <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-6 text-sm">
                <div className="font-semibold text-destructive">{t("events.ageLimitTitle", { minAge })}</div>
                <p className="mt-1 text-muted-foreground">
                  {t("events.ageLimitBody")}
                </p>
              </div>
            ) : event.is_invite_only ? (
              <div className="rounded-2xl border border-border bg-card p-5 shadow-card">
                <div className="flex items-center gap-2">
                  <Ticket className="h-4 w-4 text-primary" />
                  <h3 className="text-lg font-semibold">{t("events.inviteOnlyTitle")}</h3>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t("events.inviteOnlyBody")}
                </p>
                {event.door_min_topup_kurus > 0 && (
                  <div className="mt-3 rounded-xl border border-warning/30 bg-warning/10 p-3 text-sm">
                    <strong>{t("events.minTopupNotice")}</strong>{" "}
                    {t("events.minTopupBody", { amount: (event.door_min_topup_kurus / 100).toLocaleString(locale) })}
                  </div>
                )}
                <Button
                  type="button"
                  className="mt-4 h-11 w-full sm:w-auto"
                  disabled={claimingId === "__invite__"}
                  onClick={() => void handleClaimInvite()}
                >
                  {claimingId === "__invite__" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    t("events.wantToJoin")
                  )}
                </Button>
              </div>
            ) : tickets.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border p-6 text-center text-muted-foreground">
                {t("events.noTicketTypes")}
              </div>
            ) : (
              <div className="space-y-3">
                {tickets.map((tk) => (
                  <div key={tk.id} className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-border bg-card p-5 shadow-card">
                    <div>
                      <div className="flex items-center gap-2">
                        <Ticket className="h-4 w-4 text-primary" />
                        <h3 className="text-lg font-semibold">{tc(tk.name)}</h3>
                      </div>
                      {tk.description && <p className="mt-1 text-sm text-muted-foreground">{tc(tk.description)}</p>}
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-xl font-bold">
                          {tk.price_kurus === 0 ? t("events.free") : `${(tk.price_kurus / 100).toLocaleString(locale)} ₺`}
                        </div>
                      </div>
                      {tk.coming_soon ? (
                        <span className="inline-flex items-center rounded-full border border-border bg-muted px-4 py-2 text-sm font-semibold text-muted-foreground">
                          {t("events.saleComingSoon")}
                        </span>
                      ) : (
                        <>
                          {tk.price_kurus > 0 && tk.quantity > 0 && (
                            <div className="flex items-center gap-1 rounded-full border border-border bg-background p-1">
                              <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 rounded-full"
                                onClick={() => setQty(tk, getQty(tk) - 1)}
                                disabled={getQty(tk) <= 1}
                              >
                                <Minus className="h-4 w-4" />
                              </Button>
                              <span className="w-6 text-center text-sm font-semibold tabular-nums">{getQty(tk)}</span>
                              <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 rounded-full"
                                onClick={() => setQty(tk, getQty(tk) + 1)}
                                disabled={getQty(tk) >= tk.quantity}
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                          <Button
                            type="button"
                            disabled={tk.quantity === 0 || claimingId === tk.id}
                            onClick={() => void handleGetTicket(tk)}
                          >
                            {claimingId === tk.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : tk.price_kurus === 0 ? (
                              t("events.getTicketFree")
                            ) : (
                              t("events.getTicket")
                            )}
                          </Button>
                        </>
                      )}
                    </div>

                  </div>
                ))}
              </div>
            )}
          </div>
        </div>


        <section className="mt-12 rounded-2xl border border-border bg-card p-6 shadow-card">
          <div className="mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">{t("events.eventRules")}</h2>
          </div>
          <ul className="space-y-2 text-sm leading-relaxed text-muted-foreground">
            {activeRules.map((rule, i) => (
              <li key={i} className="flex gap-2">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                <span>{i === 0 && minAge > 18 ? t("events.ageLimitRule", { minAge }) : rule}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
